import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Loader2,
  Search,
  Calendar,
  User,
  Users,
  Filter,
  CheckSquare,
  Square,
  ThumbsUp,
  ThumbsDown,
  FileText,
  Edit,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: FileText },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const OvertimeManagement = () => {
  const { token, user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'employee'
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  
  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Modals
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [approvalForm, setApprovalForm] = useState({
    approved: true,
    approved_hours: 0,
    notes: ''
  });
  const [editForm, setEditForm] = useState({
    clock_in_time: '',
    clock_out_time: '',
    overtime_hours: 0,
    notes: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    loadOvertimeEntries();
  }, []);

  const loadOvertimeEntries = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/api/timeclock/overtime/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEntries(response.data.entries || []);
      setSummary(response.data.summary || null);
      setSelectedIds([]);
      setSelectAll(false);
    } catch (error) {
      toast.error('Failed to load overtime entries');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique employees for filter
  const uniqueEmployees = [...new Set(entries.map(e => e.user_name))].sort();

  // Filter entries
  const filteredEntries = entries.filter(entry => {
    // Tab filter
    if (activeTab === 'pending' && !['draft', 'pending'].includes(entry.status)) return false;
    if (activeTab === 'approved' && entry.status !== 'approved') return false;
    if (activeTab === 'rejected' && entry.status !== 'rejected') return false;
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!entry.user_name?.toLowerCase().includes(term)) return false;
    }
    
    // Date filter
    if (dateFilter && entry.date !== dateFilter) return false;
    
    // Employee filter
    if (employeeFilter !== 'all' && entry.user_name !== employeeFilter) return false;
    
    return true;
  });

  // Group entries by employee
  const entriesByEmployee = filteredEntries.reduce((acc, entry) => {
    const name = entry.user_name || 'Unknown';
    if (!acc[name]) {
      acc[name] = {
        user_name: name,
        user_id: entry.user_id,
        entries: [],
        totalOvertimeHours: 0,
        totalApprovedHours: 0,
        draftCount: 0,
        approvedCount: 0
      };
    }
    acc[name].entries.push(entry);
    acc[name].totalOvertimeHours += entry.overtime_hours || 0;
    if (entry.status === 'approved') {
      acc[name].totalApprovedHours += entry.approved_hours || entry.overtime_hours || 0;
      acc[name].approvedCount++;
    }
    if (['draft', 'pending'].includes(entry.status)) {
      acc[name].draftCount++;
    }
    return acc;
  }, {});

  // Selection handlers
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const pendingIds = filteredEntries
        .filter(e => ['draft', 'pending'].includes(e.status))
        .map(e => e.id);
      setSelectedIds(pendingIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectEntry = (entryId, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, entryId]);
    } else {
      setSelectedIds(selectedIds.filter(id => id !== entryId));
      setSelectAll(false);
    }
  };

  // Edit handler
  const openEditModal = (entry) => {
    setSelectedEntry(entry);
    setEditForm({
      clock_in_time: entry.clock_in_time || '',
      clock_out_time: entry.clock_out_time || '',
      overtime_hours: entry.overtime_hours || 0,
      notes: entry.notes || ''
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedEntry) return;
    
    setIsSaving(true);
    try {
      await axios.put(
        `${API}/api/timeclock/overtime/${selectedEntry.id}`,
        {
          clock_in_time: editForm.clock_in_time,
          clock_out_time: editForm.clock_out_time,
          overtime_hours: editForm.overtime_hours,
          notes: editForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Overtime entry updated');
      setIsEditModalOpen(false);
      loadOvertimeEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update overtime');
    } finally {
      setIsSaving(false);
    }
  };

  // Recalculate overtime hours when times change
  const recalculateOvertime = (clockIn, clockOut) => {
    try {
      const [inH, inM] = clockIn.split(':').map(Number);
      const [outH, outM] = clockOut.split(':').map(Number);
      const totalMinutes = (outH * 60 + outM) - (inH * 60 + inM);
      const totalHours = totalMinutes / 60;
      const overtime = Math.max(0, totalHours - 8);
      return Math.round(overtime * 100) / 100;
    } catch {
      return editForm.overtime_hours;
    }
  };

  // Approval handlers
  const openApproveModal = (entry, approve = true) => {
    setSelectedEntry(entry);
    setApprovalForm({
      approved: approve,
      approved_hours: entry.overtime_hours,
      notes: ''
    });
    setIsApproveModalOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedEntry) return;
    
    setIsSaving(true);
    try {
      await axios.post(
        `${API}/api/timeclock/overtime/${selectedEntry.id}/approve`,
        {
          approved: approvalForm.approved,
          approved_hours: approvalForm.approved_hours,
          notes: approvalForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(approvalForm.approved ? 'Overtime approved!' : 'Overtime rejected');
      setIsApproveModalOpen(false);
      loadOvertimeEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process overtime');
    } finally {
      setIsSaving(false);
    }
  };

  const openBulkModal = (approve = true) => {
    if (selectedIds.length === 0) {
      toast.error('No entries selected');
      return;
    }
    setApprovalForm({
      approved: approve,
      approved_hours: null,
      notes: ''
    });
    setIsBulkModalOpen(true);
  };

  const handleBulkApprove = async () => {
    setIsSaving(true);
    try {
      const response = await axios.post(
        `${API}/api/timeclock/overtime/bulk-approve`,
        {
          entry_ids: selectedIds,
          approved: approvalForm.approved,
          approved_hours: approvalForm.approved_hours,
          notes: approvalForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const action = approvalForm.approved ? 'approved' : 'rejected';
      const count = approvalForm.approved ? response.data.approved : response.data.rejected;
      toast.success(`${count} overtime entries ${action}`);
      setIsBulkModalOpen(false);
      loadOvertimeEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process overtime');
    } finally {
      setIsSaving(false);
    }
  };

  const handleScanOvertime = async () => {
    if (!window.confirm('This will scan all timesheets from the last 30 days and create overtime entries for staff who worked more than 8 hours. Continue?')) {
      return;
    }
    
    setIsScanning(true);
    try {
      const response = await axios.post(
        `${API}/api/timeclock/scan-overtime`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.message}`);
      loadOvertimeEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan overtime');
    } finally {
      setIsScanning(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-PH', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if user can approve
  const canApprove = user?.role === 'admin' || 
    ['arbe', 'alma', 'pabs', 'jelyn'].some(name => 
      user?.username?.toLowerCase().includes(name) || 
      user?.full_name?.toLowerCase().includes(name)
    );

  // Render entry row
  const renderEntryRow = (entry, showEmployee = true) => {
    const statusConfig = STATUS_CONFIG[entry.status] || STATUS_CONFIG.draft;
    const StatusIcon = statusConfig.icon;
    const isPending = ['draft', 'pending'].includes(entry.status);
    const isSelected = selectedIds.includes(entry.id);

    return (
      <div 
        key={entry.id}
        className={`p-3 border rounded-lg bg-white ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
      >
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          {/* Selection Checkbox */}
          {isPending && canApprove && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => handleSelectEntry(entry.id, checked)}
            />
          )}

          {/* Date & Employee */}
          <div className="flex items-center gap-3 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${statusConfig.color}`}>
              <StatusIcon size={16} />
            </div>
            <div>
              <p className="font-medium text-slate-800">{formatDate(entry.date)}</p>
              {showEmployee && <p className="text-sm text-slate-500">{entry.user_name}</p>}
            </div>
          </div>

          {/* Time Info */}
          <div className="flex gap-4 text-sm">
            <div className="text-center">
              <p className="text-slate-500">Time</p>
              <p className="font-medium">{entry.clock_in_time} - {entry.clock_out_time}</p>
            </div>
            <div className="text-center">
              <p className="text-slate-500">Total</p>
              <p className="font-medium">{entry.total_hours_worked}h</p>
            </div>
            <div className="text-center">
              <p className="text-slate-500">OT</p>
              <p className="font-bold text-blue-600">{entry.overtime_hours}h</p>
            </div>
          </div>

          {/* Status & Actions */}
          <div className="flex items-center gap-2">
            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            
            {entry.status === 'approved' && (
              <Badge variant="outline" className="text-green-600">
                {entry.approved_hours}h
              </Badge>
            )}

            {isPending && canApprove && (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEditModal(entry)}
                  title="Edit"
                >
                  <Edit size={14} />
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => openApproveModal(entry, true)}
                  title="Approve"
                >
                  <ThumbsUp size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => openApproveModal(entry, false)}
                  title="Reject"
                >
                  <ThumbsDown size={14} />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Notes/Info */}
        {entry.notes && (
          <p className="mt-2 text-sm text-slate-500 italic">Note: {entry.notes}</p>
        )}
        {entry.status === 'approved' && entry.approved_by && (
          <p className="mt-2 text-xs text-green-600">
            Approved by {entry.approved_by} on {formatDateTime(entry.approved_at)}
          </p>
        )}
        {entry.status === 'rejected' && entry.rejected_by && (
          <p className="mt-2 text-xs text-red-600">
            Rejected by {entry.rejected_by}: {entry.reject_reason}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Overtime Management</h1>
          <p className="text-slate-500">Manage and approve employee overtime entries</p>
        </div>
        <div className="flex gap-2">
          {canApprove && (
            <Button 
              variant="outline" 
              onClick={handleScanOvertime} 
              disabled={isScanning}
              className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
            >
              {isScanning ? <Loader2 size={18} className="animate-spin mr-2" /> : <Search size={18} className="mr-2" />}
              Scan Timesheets
            </Button>
          )}
          <Button variant="outline" onClick={loadOvertimeEntries} disabled={isLoading}>
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card className="bg-slate-50">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-slate-700">{summary.total_entries}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-100">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-slate-600">{summary.draft}</p>
              <p className="text-xs text-slate-500">Draft</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{summary.pending}</p>
              <p className="text-xs text-amber-600">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{summary.approved}</p>
              <p className="text-xs text-green-600">Approved</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-700">{summary.rejected}</p>
              <p className="text-xs text-red-600">Rejected</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{summary.total_approved_hours}h</p>
              <p className="text-xs text-blue-600">Approved Hours</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Mode Toggle */}
      <div className="flex items-center gap-4">
        <div className="flex bg-slate-100 rounded-lg p-1">
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <Calendar size={16} className="mr-1" /> List View
          </Button>
          <Button
            variant={viewMode === 'employee' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('employee')}
          >
            <Users size={16} className="mr-1" /> By Employee
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">
            <AlertCircle size={16} className="mr-1" />
            Pending ({entries.filter(e => ['draft', 'pending'].includes(e.status)).length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            <CheckCircle size={16} className="mr-1" />
            Approved ({entries.filter(e => e.status === 'approved').length})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            <XCircle size={16} className="mr-1" />
            Rejected ({entries.filter(e => e.status === 'rejected').length})
          </TabsTrigger>
          <TabsTrigger value="all">All ({entries.length})</TabsTrigger>
        </TabsList>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              placeholder="Search by employee name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by employee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {uniqueEmployees.map(name => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-full sm:w-40"
          />
          {activeTab === 'pending' && selectedIds.length > 0 && canApprove && (
            <div className="flex gap-2">
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => openBulkModal(true)}>
                <ThumbsUp size={16} className="mr-1" /> Approve ({selectedIds.length})
              </Button>
              <Button variant="destructive" onClick={() => openBulkModal(false)}>
                <ThumbsDown size={16} className="mr-1" /> Reject
              </Button>
            </div>
          )}
        </div>

        {/* Content */}
        <TabsContent value={activeTab} className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={32} className="animate-spin text-blue-600" />
            </div>
          ) : filteredEntries.length === 0 ? (
            <Card className="bg-white">
              <CardContent className="p-10 text-center">
                <Clock size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No overtime entries found</p>
                {canApprove && (
                  <Button className="mt-4" variant="outline" onClick={handleScanOvertime}>
                    <Search size={16} className="mr-2" /> Scan Timesheets for Overtime
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : viewMode === 'list' ? (
            /* LIST VIEW */
            <div className="space-y-2">
              {activeTab === 'pending' && canApprove && (
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                  <span className="text-sm text-slate-600">Select All Pending</span>
                </div>
              )}
              {filteredEntries.map(entry => renderEntryRow(entry, true))}
            </div>
          ) : (
            /* EMPLOYEE VIEW */
            <div className="space-y-4">
              {Object.values(entriesByEmployee).map(emp => (
                <Card key={emp.user_id} className="bg-white">
                  <Accordion type="single" collapsible>
                    <AccordionItem value={emp.user_id} className="border-0">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline">
                        <div className="flex items-center justify-between w-full pr-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <User size={20} className="text-blue-600" />
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-slate-800">{emp.user_name}</p>
                              <p className="text-sm text-slate-500">{emp.entries.length} entries</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-center">
                              <p className="text-lg font-bold text-blue-600">{emp.totalOvertimeHours.toFixed(1)}h</p>
                              <p className="text-xs text-slate-500">Total OT</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-bold text-green-600">{emp.totalApprovedHours.toFixed(1)}h</p>
                              <p className="text-xs text-slate-500">Approved</p>
                            </div>
                            {emp.draftCount > 0 && (
                              <Badge className="bg-amber-100 text-amber-700">
                                {emp.draftCount} pending
                              </Badge>
                            )}
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-2 mt-2">
                          {emp.entries
                            .sort((a, b) => b.date.localeCompare(a.date))
                            .map(entry => renderEntryRow(entry, false))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={20} /> Edit Overtime Entry
            </DialogTitle>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold">{selectedEntry.user_name}</p>
                <p className="text-sm text-slate-500">{formatDate(selectedEntry.date)}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Clock In Time</Label>
                  <Input
                    type="time"
                    value={editForm.clock_in_time}
                    onChange={(e) => {
                      const newClockIn = e.target.value;
                      const newOT = recalculateOvertime(newClockIn, editForm.clock_out_time);
                      setEditForm({...editForm, clock_in_time: newClockIn, overtime_hours: newOT});
                    }}
                  />
                </div>
                <div>
                  <Label>Clock Out Time</Label>
                  <Input
                    type="time"
                    value={editForm.clock_out_time}
                    onChange={(e) => {
                      const newClockOut = e.target.value;
                      const newOT = recalculateOvertime(editForm.clock_in_time, newClockOut);
                      setEditForm({...editForm, clock_out_time: newClockOut, overtime_hours: newOT});
                    }}
                  />
                </div>
              </div>

              <div>
                <Label>Overtime Hours</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={editForm.overtime_hours}
                  onChange={(e) => setEditForm({...editForm, overtime_hours: parseFloat(e.target.value) || 0})}
                />
                <p className="text-xs text-slate-500 mt-1">Auto-calculated from times, but you can adjust manually</p>
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Add any notes..."
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600" onClick={handleSaveEdit} disabled={isSaving}>
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} className="mr-1" /> Save Changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={isApproveModalOpen} onOpenChange={setIsApproveModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{approvalForm.approved ? 'Approve Overtime' : 'Reject Overtime'}</DialogTitle>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold">{selectedEntry.user_name}</p>
                <p className="text-sm text-slate-500">{formatDate(selectedEntry.date)}</p>
                <p className="text-sm">{selectedEntry.clock_in_time} - {selectedEntry.clock_out_time}</p>
                <p className="font-bold text-blue-600 mt-1">Overtime: {selectedEntry.overtime_hours}h</p>
              </div>

              {approvalForm.approved && (
                <div>
                  <Label>Approved Hours</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={approvalForm.approved_hours}
                    onChange={(e) => setApprovalForm({...approvalForm, approved_hours: parseFloat(e.target.value) || 0})}
                  />
                </div>
              )}

              <div>
                <Label>{approvalForm.approved ? 'Notes (Optional)' : 'Rejection Reason'}</Label>
                <Textarea
                  value={approvalForm.notes}
                  onChange={(e) => setApprovalForm({...approvalForm, notes: e.target.value})}
                  placeholder={approvalForm.approved ? "Add notes..." : "Enter reason..."}
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsApproveModalOpen(false)}>Cancel</Button>
            <Button
              className={approvalForm.approved ? 'bg-green-600' : 'bg-red-600'}
              onClick={handleApprove}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : approvalForm.approved ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Modal */}
      <Dialog open={isBulkModalOpen} onOpenChange={setIsBulkModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk {approvalForm.approved ? 'Approve' : 'Reject'} Overtime</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-slate-600">
              {approvalForm.approved ? 'Approve' : 'Reject'} <strong>{selectedIds.length}</strong> overtime entries?
            </p>

            {approvalForm.approved && (
              <div>
                <Label>Override Hours (Optional)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={approvalForm.approved_hours || ''}
                  onChange={(e) => setApprovalForm({...approvalForm, approved_hours: e.target.value ? parseFloat(e.target.value) : null})}
                  placeholder="Leave empty to use original hours"
                />
              </div>
            )}

            <div>
              <Label>{approvalForm.approved ? 'Notes (Optional)' : 'Rejection Reason'}</Label>
              <Textarea
                value={approvalForm.notes}
                onChange={(e) => setApprovalForm({...approvalForm, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkModalOpen(false)}>Cancel</Button>
            <Button
              className={approvalForm.approved ? 'bg-green-600' : 'bg-red-600'}
              onClick={handleBulkApprove}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : `${approvalForm.approved ? 'Approve' : 'Reject'} ${selectedIds.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h3 className="font-semibold text-blue-800 mb-2">How Overtime Works</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• When an employee works more than <strong>8 hours</strong>, overtime is automatically calculated</li>
            <li>• Overtime entries are created as <strong>"Draft"</strong> status when the employee clocks out</li>
            <li>• Only <strong>Alma, Arbe, Pabs, or Jelyn</strong> can edit/approve/reject overtime</li>
            <li>• Use <strong>"Edit"</strong> button to modify time or hours before approval</li>
            <li>• Approved overtime will be included in payroll calculations</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default OvertimeManagement;
